import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.jsx';
import { Rating } from '../../components/Rating.jsx';
import { INSIDER_LABEL, bookableTimes, isSoldOut } from '../../data/membership.js';
import { usePlaceDetails } from '../../hooks/usePlaceDetails.js';
import { useTravelTime } from '../../hooks/useTravelTime.js';
import { formatTravelLabel, preferredTravelMode } from '../../lib/travelTime.js';
import { color, font, radius, shadow } from '../../theme/tokens.js';
import { PhotoCarousel } from './PhotoCarousel.jsx';
import { SwipeCardBack } from './SwipeCardBack.jsx';
import { useSwipeGesture } from './useSwipeGesture.js';

/** Scale and vertical offset for each visible position in the stack. */
const STACK_SCALE = [1, 0.956, 0.915];
const STACK_OFFSET_Y = [0, 14, 26];

/**
 * One card in the discovery deck.
 *
 * Drag it horizontally to vote, or tap it to flip to the detail side. Only the
 * card at `stackIndex === 0` is interactive; the rest are decorative depth.
 *
 * @param {object} props
 * @param {import('../../lib/types.js').Restaurant} props.restaurant
 * @param {number} props.stackIndex  0 is the top, front-most card.
 * @param {import('../../lib/types.js').MemberTier} props.tier
 * @param {(direction: 'left'|'right', restaurant: object, meta: {flipped: boolean}) => void} props.onSwipe
 * @param {'left'|'right'|null} [props.triggerSwipe]  Fires a swipe from the action buttons.
 * @param {(restaurant: object, mode: 'walking'|'driving') => void} props.onShowDirections
 * @param {[number, number]|null} [props.origin]  Where distances are measured from.
 * @param {number} [props.groupVotes]
 * @param {number} [props.groupSize]
 * @param {boolean} [props.compact]  Smaller card chrome for embedded iMessage web views.
 */
export function SwipeCard({
  restaurant,
  stackIndex,
  tier,
  onSwipe,
  triggerSwipe,
  onShowDirections,
  origin = null,
  groupVotes = 0,
  groupSize = 0,
  compact = false,
}) {
  const isTop = stackIndex === 0;
  const [flipped, setFlipped] = useState(false);
  const everFlipped = useRef(false);
  const [flipping, setFlipping] = useState(false);

  const times = bookableTimes(restaurant, tier);
  const soldOut = isSoldOut(restaurant, tier);
  const showsInsiderBadge = restaurant.memberExclusive && times.length > 0;

  // Only fetch imagery/details/travel time for cards about to be seen, to
  // limit API calls.
  const { details, photoUrls, photoAttributions } = usePlaceDetails(restaurant, {
    enabled: stackIndex <= 1,
  });
  const travelTime = useTravelTime(restaurant, { enabled: stackIndex <= 1, origin });

  const { offsetX, flyDirection, isDragging, fly, handlers } = useSwipeGesture({
    enabled: isTop && !flipped,
    onCommit: (direction) => onSwipe(direction, restaurant, { flipped: everFlipped.current }),
    onTap: () => toggleFlip(),
  });

  // Fired by the round action buttons beneath the deck.
  useEffect(() => {
    if (triggerSwipe && isTop) fly(triggerSwipe);
  }, [triggerSwipe, isTop, fly]);

  // Reset presentation when this component is reused for a new stack position.
  useEffect(() => {
    setFlipped(false);
    setFlipping(false);
  }, [stackIndex]);

  /** Squashes the card horizontally, swaps faces at the midpoint, expands back. */
  function toggleFlip(force) {
    setFlipping(true);
    window.setTimeout(() => {
      setFlipped((current) => {
        const next = force ?? !current;
        if (next) everFlipped.current = true;
        return next;
      });
      setFlipping(false);
    }, 150);
  }

  const voteOpacity = isTop ? Math.min(Math.max(offsetX, 0) / 80, 1) : 0;
  const passOpacity = isTop ? Math.min(Math.max(-offsetX, 0) / 80, 1) : 0;

  return (
    <div
      {...handlers}
      onClick={(event) => {
        // Dragging is deliberately switched off while flipped (no accidental
        // vote while reading reviews), but a tap should still flip back —
        // `useSwipeGesture`'s handlers are disabled wholesale by `enabled`,
        // so tap-to-flip-back needs its own path here instead.
        if (flipped) {
          toggleFlip(false);
          return;
        }
        handlers.onClick?.(event);
      }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30 - stackIndex,
        transformOrigin: 'bottom center',
        userSelect: 'none',
        // Tells the browser this element owns the gesture, so iOS doesn't try
        // to interpret a diagonal drag as a scroll/bounce at the same time.
        touchAction: 'none',
        cursor: isTop ? (isDragging ? 'grabbing' : 'grab') : 'default',
        ...stackTransform({ stackIndex, isTop, offsetX, flyDirection, isDragging, compact }),
      }}
    >
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: color.surface,
          borderRadius: radius.xl,
          boxShadow: shadow.card,
          overflow: 'hidden',
          transform: flipping ? 'scaleX(0.03)' : 'scaleX(1)',
          transition: 'transform 0.14s ease',
        }}
      >
        {flipped ? (
          <SwipeCardBack
            restaurant={restaurant}
            compact={compact}
            onFlipBack={() => toggleFlip(false)}
          />
        ) : (
          <>
            <div
              style={{
                flex: compact ? '1 1 auto' : '0 0 68%',
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <PhotoCarousel
                restaurant={restaurant}
                photoUrls={photoUrls}
                photoAttributions={photoAttributions}
                interactive={isTop}
                indicatorPosition="top"
              />

              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(to bottom, rgba(0,0,0,0) 30%, rgba(0,0,0,0.72) 100%)',
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  position: 'absolute',
                  top: compact ? 10 : 14,
                  left: compact ? 10 : 14,
                  display: 'flex',
                  gap: 6,
                }}
              >
                {showsInsiderBadge && <Badge tone="brand">{INSIDER_LABEL.toUpperCase()}</Badge>}
                {restaurant.rating >= 4.5 && <Badge tone="glass">TOP RATED</Badge>}
                {groupVotes > 0 && (
                  <Badge tone="success">
                    {groupVotes}/{groupSize} want this
                  </Badge>
                )}
              </div>

              {restaurant.ambiance[0] && (
                <div
                  style={{ position: 'absolute', top: compact ? 10 : 14, right: compact ? 10 : 14 }}
                >
                  <Badge tone="glass" capitalize>
                    {restaurant.ambiance[0]}
                  </Badge>
                </div>
              )}

              <VoteStamp
                label={soldOut ? 'NOTIFY' : 'DINE'}
                tone={color.success}
                opacity={voteOpacity}
                side="left"
              />
              <VoteStamp label="PASS" tone={color.danger} opacity={passOpacity} side="right" />

              <div style={{ position: 'absolute', bottom: 14, left: 16, right: 16 }}>
                <h2
                  style={{
                    margin: 0,
                    color: color.inkInverse,
                    fontSize: compact ? 18 : 22,
                    fontWeight: font.weight.heavy,
                    letterSpacing: '-0.4px',
                    textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                  }}
                >
                  {restaurant.name}
                </h2>
                <p
                  style={{
                    margin: '3px 0 0',
                    color: 'rgba(255,255,255,0.78)',
                    fontSize: compact ? 11 : 12,
                    fontWeight: font.weight.medium,
                  }}
                >
                  {restaurant.neighborhood} · {restaurant.cuisine}
                </p>
              </div>
            </div>

            <div
              style={{
                flex: compact ? '0 0 auto' : 1,
                padding: compact ? '7px 10px' : '10px 14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: compact ? 'flex-start' : 'space-between',
                gap: compact ? 11 : 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Rating value={restaurant.rating} />
                <Dot />
                <span
                  style={{
                    fontSize: compact ? 11 : 12,
                    fontWeight: font.weight.semibold,
                    color: color.inkMuted,
                  }}
                >
                  {restaurant.price}
                </span>
                <Dot />
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onShowDirections?.(restaurant, preferredTravelMode(travelTime));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: compact ? 11 : 12,
                    color: color.inkFaint,
                    textDecoration: 'underline',
                    textDecorationColor: color.border,
                    textUnderlineOffset: 2,
                    cursor: 'pointer',
                  }}
                >
                  {formatTravelLabel(travelTime)}
                </button>
                {details?.phone && (
                  <>
                    <Dot />
                    <a
                      href={`tel:${details.phone.replace(/[^+0-9]/g, '')}`}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Call ${restaurant.name}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: compact ? 10 : 11,
                        color: color.inkFaint,
                        textDecoration: 'none',
                      }}
                    >
                      <Icon name="phone" size={10} stroke={color.inkFaint} />
                      Call
                    </a>
                  </>
                )}
                {restaurant.goodForGroups && (
                  <>
                    <Dot />
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: compact ? 10 : 11,
                        color: color.inkFaint,
                      }}
                    >
                      <Icon name="users" size={10} stroke={color.inkFaint} />
                      Groups OK
                    </span>
                  </>
                )}
              </div>

              <div>
                <p
                  style={{
                    margin: '0 0 4px',
                    fontSize: compact ? 8 : 9,
                    fontWeight: font.weight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: color.inkFaint,
                  }}
                >
                  About
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: compact ? 11 : 12,
                    lineHeight: compact ? 1.35 : 1.5,
                    color: color.inkMuted,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {restaurant.description}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Builds the transform/transition pair for a card's position or exit. */
function stackTransform({ stackIndex, isTop, offsetX, flyDirection, isDragging, compact = false }) {
  if (flyDirection) {
    const sign = flyDirection === 'right' ? 1 : -1;
    return {
      transform: `translateX(${sign * 115}vw) rotate(${sign * 22}deg)`,
      transition: 'transform 0.38s cubic-bezier(0.55,0,0.8,0.95)',
    };
  }

  if (isTop) {
    return {
      transform:
        `translateX(${offsetX}px) ` +
        `translateY(${Math.abs(offsetX) * 0.05}px) ` +
        `rotate(${offsetX * 0.045}deg)`,
      transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.175,0.885,0.32,1.275)',
    };
  }

  const compactScale = [1, 0.97, 0.94];
  const compactOffsetY = [0, 8, 14];
  const scale = compact ? (compactScale[stackIndex] ?? 0.92) : (STACK_SCALE[stackIndex] ?? 0.88);
  const offsetY = compact ? (compactOffsetY[stackIndex] ?? 20) : (STACK_OFFSET_Y[stackIndex] ?? 36);
  return {
    transform: `translateY(${offsetY}px) scale(${scale})`,
    transition: 'transform 0.3s ease',
  };
}

const Dot = () => <span style={{ color: color.border }}>·</span>;

function Badge({ tone, capitalize = false, children }) {
  const tones = {
    brand: { background: color.primary, border: 'none' },
    success: {
      background: 'rgba(31,169,113,0.9)',
      border: '1px solid rgba(255,255,255,0.2)',
    },
    glass: {
      background: 'rgba(0,0,0,0.36)',
      border: '1px solid rgba(255,255,255,0.16)',
      backdropFilter: 'blur(8px)',
    },
  };

  return (
    <div style={{ borderRadius: radius.sm, padding: '3px 9px', ...tones[tone] }}>
      <span
        style={{
          color: color.inkInverse,
          fontSize: 9,
          fontWeight: font.weight.heavy,
          letterSpacing: '0.5px',
          textTransform: capitalize ? 'capitalize' : undefined,
        }}
      >
        {children}
      </span>
    </div>
  );
}

/** The rotated "DINE"/"PASS" stamp that fades in as you drag. */
function VoteStamp({ label, tone, opacity, side }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 20,
        [side]: 16,
        opacity,
        border: `3px solid ${tone}`,
        borderRadius: 9,
        padding: '4px 12px',
        color: tone,
        fontSize: 20,
        fontWeight: font.weight.black,
        letterSpacing: 2,
        transform: `rotate(${side === 'left' ? -14 : 14}deg)`,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}
